import EditIcon from "@mui/icons-material/Edit";
import RefreshIcon from "@mui/icons-material/Refresh";
import SearchIcon from "@mui/icons-material/Search";
import {
  Box,
  Button,
  IconButton,
  InputAdornment,
  MenuItem,
  TableCell,
  TableRow,
  TextField,
} from "@mui/material";
import { useEnvironmentType } from "apps/web/src/contexts/useEnvType";
import { getNavStaffOrgPath } from "apps/web/src/pages/Auth/Staff/Org/OrgRouter";
import { formatDistanceToNow } from "date-fns";
import { enUS } from "date-fns/locale";
import { useState } from "react";
import { useNavigate } from "react-router-dom-v5-compat";
import { Loading } from "shared-ui/global/Loading";
import TablePage from "../../../components/universal/TablePage";
import {
  IntegrationType,
  OrgOffsetConnectionQuery,
  StaffDashboardOrgPreviewFragment,
  useRefreshTeamworksSyncStatusMutation,
} from "../../../types";
import { PaginationHooks } from "../../../utils/pagination";

const BONAPPETIT: IntegrationType = "bonAppetit";
const CBORD: IntegrationType = "cbord";
const COMPASS: IntegrationType = "compass";

interface OrgsTableProps {
  offsetConnectionData?: OrgOffsetConnectionQuery;
  loading: boolean;
  paginationHooks: PaginationHooks;
  orgFilter: "all" | "active" | "inactive";
  onOrgFilterChange: (value: "all" | "active" | "inactive") => void;
  onEditOrg: (org: StaffDashboardOrgPreviewFragment) => void;
  onCreateOrg: () => void;
  onRefreshSyncStatus: () => void;
}

export const OrgsTable = ({
  offsetConnectionData,
  loading,
  paginationHooks,
  orgFilter,
  onOrgFilterChange,
  onEditOrg,
  onCreateOrg,
  onRefreshSyncStatus,
}: OrgsTableProps) => {
  const navigate = useNavigate();
  const { queryText, onChangeQueryText } = paginationHooks;
  const { isCommercial } = useEnvironmentType();

  const [refreshingOrgIds, setRefreshingOrgIds] = useState<string[]>([]);
  const [refreshSyncStatus] = useRefreshTeamworksSyncStatusMutation({
    onCompleted: data => {
      setRefreshingOrgIds(ids => ids.filter(id => id !== data.refreshTeamworksSyncStatus.org.id));
      onRefreshSyncStatus();
    },
  });

  const Header = (
    <Box sx={{ display: "flex", justifyContent: "flex-start", alignItems: "flex-start", gap: 2 }}>
      <TextField
        sx={{ width: 150, mt: 0 }}
        select
        value={orgFilter}
        onChange={e => onOrgFilterChange(e.target.value as "all" | "active" | "inactive")}
      >
        <MenuItem value="active">Active</MenuItem>
        <MenuItem value="inactive">Inactive</MenuItem>
        <MenuItem value="all">All</MenuItem>
      </TextField>

      <TextField
        sx={{ mt: 0 }}
        placeholder="Search"
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchIcon />
            </InputAdornment>
          ),
        }}
        value={queryText}
        onChange={e => onChangeQueryText(e.target.value)}
      />
      <Button onClick={onCreateOrg}>Create Org</Button>
    </Box>
  );

  const TableHeaderRow = () => (
    <TableRow>
      <TableCell>Edit</TableCell>
      <TableCell>Name</TableCell>
      <TableCell>Locale</TableCell>
      <TableCell>Territory</TableCell>
      <TableCell>Active</TableCell>
      <TableCell>Public Access</TableCell>
      <TableCell>Axle</TableCell>
      {isCommercial && (
        <>
          <TableCell>Teamworks</TableCell>
          <TableCell>Notemeal Linked</TableCell>
          <TableCell>Teamworks Linked</TableCell>
          <TableCell>Fully Synced</TableCell>
          <TableCell>Compass</TableCell>
          <TableCell>CBORD</TableCell>
          <TableCell>Bon Appetit</TableCell>
          <TableCell>Last Updated</TableCell>
          <TableCell>Refresh Sync Status</TableCell>
        </>
      )}
      <TableCell>Agreement Form</TableCell>
      <TableCell>Scoring System</TableCell>
    </TableRow>
  );

  const tableBodyRows = offsetConnectionData
    ? offsetConnectionData.orgOffsetConnection.edges.map(org => {
        const compassIntegration = !!org.integrations.find(i => i.type === COMPASS);
        const cbordIntegration = !!org.integrations.find(i => i.type === CBORD);
        const bonAppetitIntegration = !!org.integrations.find(i => i.type === BONAPPETIT);
        const { teamworksSyncStatus } = org;

        return (
          <TableRow
            sx={{
              "&:hover": {
                cursor: "pointer",
                backgroundColor: "greyscale.100",
              },
            }}
            onClick={() => navigate(getNavStaffOrgPath(org.id))}
            key={org.id}
          >
            <TableCell scope="row">
              <IconButton
                onClick={e => {
                  e.preventDefault();
                  e.stopPropagation();
                  onEditOrg(org);
                }}
                size="large"
              >
                <EditIcon fontSize="small" />
              </IconButton>
            </TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }} scope="row">
              {org.name}
            </TableCell>
            <TableCell>{org.localeCode}</TableCell>
            <TableCell>{org.territoryName}</TableCell>
            <TableCell>{org.isActive && "✓"}</TableCell>
            <TableCell>{org.allowsPublicAccess && "✓"}</TableCell>
            <TableCell style={{ whiteSpace: "nowrap" }}>{`${org.axleOrgDocumentId ? org.axleOrgDocumentId : ""} ${
              org.isAxleSyncEnabled ? "🔄" : ""
            }`}</TableCell>
            {isCommercial && (
              <>
                <TableCell>{org.teamworksId !== null && "✓"}</TableCell>
                <TableCell>{org.isNotemealLinked && "✓"}</TableCell>
                <TableCell>{teamworksSyncStatus?.isTeamworksLinked && "✓"}</TableCell>
                <TableCell>{teamworksSyncStatus?.isFullySynced && "✓"}</TableCell>
                <TableCell>{compassIntegration && "✓"}</TableCell>
                <TableCell>{cbordIntegration && "✓"}</TableCell>
                <TableCell>{bonAppetitIntegration && "✓"}</TableCell>
                <TableCell>
                  {/* TODO: Locale Cleanup Staff page */}
                  {teamworksSyncStatus &&
                    formatDistanceToNow(new Date(teamworksSyncStatus.updatedAt), { addSuffix: true, locale: enUS })}
                </TableCell>
                <TableCell>
                  <IconButton
                    size="small"
                    onClick={e => {
                      e.preventDefault();
                      e.stopPropagation();

                      if (refreshingOrgIds.includes(org.id)) {
                        return;
                      }
                      setRefreshingOrgIds(ids => [...ids, org.id]);
                      refreshSyncStatus({
                        variables: {
                          input: {
                            orgId: org.id,
                          },
                        },
                      });
                    }}
                  >
                    {refreshingOrgIds.includes(org.id) ? <Loading progressSize="xs" /> : <RefreshIcon />}
                  </IconButton>
                </TableCell>
              </>
            )}
            <TableCell>{org.agreementForm && "✓"}</TableCell>
            <TableCell>{org.scoringSystem?.name && "✓"}</TableCell>
          </TableRow>
        );
      })
    : null;

  return (
    <TablePage
      header={Header}
      tableHeaderRow={<TableHeaderRow />}
      tableBodyRows={tableBodyRows}
      loading={loading}
      paginationHooks={paginationHooks}
      total={offsetConnectionData?.orgOffsetConnection.pageInfo.total || 0}
      headerAlignItems="flex-start"
    />
  );
};
